/** Standards-data fallback only for the actual unsupported MK capabilities.
 * Native constructors, Persian calendar and the other twelve release locales
 * are never replaced. Preparation is awaited by boot/language switching.
 */
type MkProviders = typeof import("./mk-intl");
let providers: MkProviders | undefined;
let preparation: Promise<void> | undefined;

const isMk = (tag: string): boolean => new Intl.Locale(tag).language === "mk";
type LocaleConstructor = {
  new (tag: string): { resolvedOptions(): { locale: string } };
  supportedLocalesOf(locales: string[]): string[];
};
const nativeSupports = (
  Constructor: LocaleConstructor,
  tag: string,
): boolean => Constructor.supportedLocalesOf([tag]).length > 0
  && new Intl.Locale(new Constructor(tag).resolvedOptions().locale).language === new Intl.Locale(tag).language;

export const prepareLocaleFormatters = (tag: string): Promise<void> => {
  if (!isMk(tag) || [Intl.NumberFormat, Intl.DateTimeFormat, Intl.ListFormat].every((Constructor) => nativeSupports(Constructor, tag))) {
    return Promise.resolve();
  }
  if (!preparation) {
    preparation = import("./mk-intl").then((loaded) => { providers = loaded; }).catch((error) => {
      preparation = undefined;
      throw error;
    });
  }
  return preparation;
};

const readyProviders = (): MkProviders => {
  if (!providers) throw new RangeError("MK Intl data is not ready");
  return providers;
};

/** Only supplies the prepared fallback; native number construction stays central. */
export const preparedNumberFormatter = (tag: string, options?: Intl.NumberFormatOptions): Intl.NumberFormat | undefined =>
  isMk(tag) && !nativeSupports(Intl.NumberFormat, tag)
    ? readyProviders().numberFormatter(tag, options)
    : undefined;

export const localeDateFormatter = (tag: string, options?: Intl.DateTimeFormatOptions): Intl.DateTimeFormat =>
  isMk(tag) && !nativeSupports(Intl.DateTimeFormat, tag)
    ? readyProviders().dateFormatter(tag, options)
    : new Intl.DateTimeFormat(tag, options);

export const localeListFormatter = (tag: string, options?: Intl.ListFormatOptions): Intl.ListFormat =>
  isMk(tag) && !nativeSupports(Intl.ListFormat, tag)
    ? readyProviders().listFormatter(tag, options)
    : new Intl.ListFormat(tag, options);
